import { f7, useStore } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import {
  getSubscriptionInheritanceStatusApi,
  getSubscriptionPlansApi,
} from '@/api/subscription';
import fvHeroImage from '@/assets/images/subscription/fv-hero.png';
import fvOfferBanner from '@/assets/images/subscription/fv-offer-banner.png';
import fvStatIconLeft from '@/assets/images/subscription/fv-stat-icon-1.png';
import fvStatIconRight from '@/assets/images/subscription/fv-stat-icon-2.png';
import fvSubImage from '@/assets/images/subscription/fv-sub.png';
import supportCarlifeAssist from '@/assets/images/subscription/support-carlife-assist.png';
import supportLightRepair from '@/assets/images/subscription/support-light-repair.png';
import supportRoadService from '@/assets/images/subscription/support-road-service.png';
import { Paper } from '@/components/uiParts/Card';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { Car } from '@/types/api/customerApi';
import { SubscriptionInheritanceStatus } from '@/types/api/subscription';

interface SubscriptionInheritancePageProps {
  inheritanceStatus?: SubscriptionInheritanceStatus;
}

const formatYen = (value?: number | null) =>
  value == null ? '-' : `${value.toLocaleString()}円`;

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('ja-JP') : '-';

const problemItems = [
  {
    title: '車の修理代、突然10万円超えることも',
    description:
      'エンジン修理や電装系の故障など、部品代だけで数万円かかることがあります。',
  },
  {
    title: '小傷・へこみ、放置すると査定にひびく',
    description:
      '小さな傷やへこみも、放置すると車の印象や価値が下がる原因になります。',
  },
  {
    title: '車検・点検のたびに予想外の追加費用がかかる',
    description:
      '点検時に見つかる不具合や消耗で、予定外の出費が発生することがあります。',
  },
];

const serviceContents = [
  {
    title: '修理優待',
    description:
      'REVISE提携工場での対象整備・対象修理に、会員価格を適用できます。',
    image: supportCarlifeAssist,
    included: [
      'エンジン・駆動系・電装系・ブレーキ系・サスペンション等',
      '事前見積承認制で、費用を確認してから修理へ進めます',
      '部品は中古、リビルト、社外新品、純正の優先順でご提案します',
    ],
    excluded: [
      '消耗品（タイヤ・バッテリー・ブレーキパッド等）',
      '事故の鈑金全面修理・社外品・競技用部品',
    ],
  },
  {
    title: '軽鈑金修理',
    description:
      '小傷や軽微なへこみなど、日常で気になる外装補修も相談できます。',
    image: supportLightRepair,
    included: [
      '小傷・ガラスリペア・軽微なへこみ・ホイール補修',
      '事前見積承認制で、修理前に金額を確認できます',
    ],
    excluded: ['大事故修理・部品交換を伴う大規模修理'],
  },
  {
    title: 'ロードサービス',
    description:
      '出先でのトラブル時にも、レッカー搬送や応急対応を相談できます。',
    image: supportRoadService,
    included: [
      'レッカー50kmまでの無料搬送',
      '鍵開け・バッテリー上がり・ガス欠・パンク時の応急対応',
      '24時間365日対応',
    ],
    excluded: ['事故処理・レッカー50km超過分の搬送費用'],
  },
];

const heroTags = ['全車種OK', '加入当日から', 'アプリ完結'];

const SectionHead: FC<{ enTitle: string; jaTitle: string }> = ({
  enTitle,
  jaTitle,
}) => (
  <div
    style={{
      textAlign: 'center',
      paddingBottom: '14px',
      marginBottom: '24px',
      position: 'relative',
    }}
  >
    <div
      style={{
        fontSize: '32px',
        fontWeight: 700,
        color: '#1E55E6',
        lineHeight: 1.3,
      }}
    >
      {enTitle}
    </div>
    <div style={{ fontWeight: 700, lineHeight: 1.5, color: '#222' }}>
      {jaTitle}
    </div>
    <div
      style={{
        position: 'absolute',
        width: '26px',
        height: '2px',
        backgroundColor: '#1E55E6',
        bottom: 0,
        left: 'calc(50% - 13px)',
      }}
    />
  </div>
);

export const SubscriptionInheritancePage: FC<
  SubscriptionInheritancePageProps
> = ({ inheritanceStatus: propInheritanceStatus }) => {
  const authInfo = useStore(store, 'authInfo') as any;
  const selectedCar = useStore(store, 'carItemInfo') as Car;
  const [inheritanceStatus, setInheritanceStatus] =
    useState<SubscriptionInheritanceStatus | null>(
      propInheritanceStatus || null,
    );
  const [standardMonthlyFee, setStandardMonthlyFee] = useState<number | null>(
    null,
  );
  const [planError, setPlanError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(!!propInheritanceStatus);

  useEffect(() => {
    const fetchStatus = async () => {
      if (
        propInheritanceStatus ||
        !authInfo?.m_customer_id ||
        !selectedCar?.t_stock_car_id
      ) {
        setIsLoaded(true);
        return;
      }

      try {
        const res = await getSubscriptionInheritanceStatusApi(
          authInfo.m_customer_id,
          selectedCar.t_stock_car_id,
        );
        if (res.data.success && res.data.data) {
          setInheritanceStatus(res.data.data);
        }
      } finally {
        setIsLoaded(true);
      }
    };

    fetchStatus();
  }, [
    authInfo?.m_customer_id,
    propInheritanceStatus,
    selectedCar?.t_stock_car_id,
  ]);

  const fetchPlan = async () => {
    setPlanError(false);
    try {
      let res = await getSubscriptionPlansApi();
      if (!res?.data?.success) {
        console.warn('プラン取得: 1回目失敗、リトライ');
        res = await getSubscriptionPlansApi();
      }
      const plan = res?.data?.data?.plans?.[0];
      if (res?.data?.success && plan) {
        setStandardMonthlyFee(plan.monthly_fee);
      } else {
        console.error('プラン取得失敗', res?.data);
        setPlanError(true);
      }
    } catch (e) {
      console.warn('プラン取得: 1回目例外、リトライ', e);
      try {
        const retry = await getSubscriptionPlansApi();
        const plan = retry?.data?.data?.plans?.[0];
        if (retry?.data?.success && plan) {
          setStandardMonthlyFee(plan.monthly_fee);
        } else {
          console.error('プラン取得失敗（リトライ後）', retry?.data);
          setPlanError(true);
        }
      } catch (e2) {
        console.error('プラン取得失敗（リトライ後例外）', e2);
        setPlanError(true);
      }
    }
  };

  useEffect(() => {
    fetchPlan();
  }, []);

  const handleClickApply = () => {
    if (!inheritanceStatus?.eligible) return;
    f7.view.main.router.navigate(paths.subscriptionConfirm, {
      props: { inheritanceStatus },
    });
  };

  const carLabel = [selectedCar?.maker_nm, selectedCar?.car_nm]
    .filter(Boolean)
    .join(' ');
  const isSameCondition =
    inheritanceStatus?.condition_type === 'same_as_warranty';
  const detailItems = inheritanceStatus
    ? [
        ...(isSameCondition
          ? [
              {
                label: '元保証の月額',
                value: `${formatYen(
                  inheritanceStatus.source_monthly_billing_amount,
                )}/月`,
              },
              {
                label: '引き継ぎ月額',
                value: `${formatYen(
                  inheritanceStatus.inherited_monthly_billing_amount,
                )}/月`,
              },
              {
                label: '修理上限金額',
                value: formatYen(inheritanceStatus.repair_amount_limit),
              },
            ]
          : [
              {
                label: 'サブスク月額',
                value:
                  standardMonthlyFee == null
                    ? planError
                      ? '取得失敗'
                      : '取得中...'
                    : `${formatYen(standardMonthlyFee)}/月`,
              },
            ]),
        {
          label: '保証満期日',
          value: formatDate(inheritanceStatus.source_warranty_end_date),
        },
        {
          label: '申込有効期限',
          value: formatDate(inheritanceStatus.valid_until),
        },
      ]
    : [];

  return (
    <PageTemplate
      pageName="subscription-inheritance"
      title="マイくるプラス"
      showNavBack
    >
      <div style={{ padding: '16px' }}>
        <Paper cardCN="u-margin-top-0" cardProps={{ style: { margin: 0 } }}>
          {!isLoaded && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              読み込み中...
            </div>
          )}

          {isLoaded && inheritanceStatus?.eligible && (
            <>
              <div
                style={{
                  textAlign: 'center',
                  padding: '8px 0 24px',
                  marginBottom: '20px',
                }}
              >
                <div
                  style={{
                    display: 'inline-block',
                    padding: '5px 12px',
                    borderRadius: '999px',
                    backgroundColor: '#EAF0FF',
                    color: '#1E55E6',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    marginBottom: '14px',
                  }}
                >
                  保証満期から1か月以内限定
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#1E55E6',
                    marginBottom: '10px',
                  }}
                >
                  {isSameCondition
                    ? '今なら保証条件を引き継いで申し込めます'
                    : '保証満期後もサブスクでカーライフをサポート'}
                </div>
                <h1
                  style={{
                    margin: '0 0 14px',
                    fontSize: '28px',
                    fontWeight: 'bold',
                    lineHeight: 1.35,
                    color: '#151515',
                  }}
                >
                  クルマをもっと
                  <br />
                  手軽に楽しもう
                </h1>
                <p
                  style={{
                    margin: '0 auto 18px',
                    maxWidth: '480px',
                    fontSize: '14px',
                    lineHeight: 1.8,
                    color: '#555',
                  }}
                >
                  {isSameCondition
                    ? '通常のサブスクよりお得な引き継ぎ条件で、修理優待・キズ補修・ロードサービスを続けられます。'
                    : '保証が終わったあとも、修理優待・キズ補修・ロードサービスをアプリからまとめて利用できます。'}
                </p>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '18px',
                  }}
                >
                  <img
                    src={fvHeroImage}
                    alt="マイくるプラス アプリ画面"
                    style={{
                      width: '44%',
                      maxWidth: '170px',
                      height: 'auto',
                      display: 'block',
                    }}
                  />
                  <img
                    src={fvSubImage}
                    alt="マイくるプラス 車両画面"
                    style={{
                      width: '44%',
                      maxWidth: '170px',
                      height: 'auto',
                      display: 'block',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '16px',
                  }}
                >
                  <img
                    src={fvStatIconLeft}
                    alt=""
                    style={{ width: '18px', height: 'auto' }}
                  />
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '20px',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '11px', color: '#888' }}>
                        顧客満足度
                      </div>
                      <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
                        92%
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#888' }}>
                        修理対応実績
                      </div>
                      <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
                        30万件超
                      </div>
                    </div>
                  </div>
                  <img
                    src={fvStatIconRight}
                    alt=""
                    style={{
                      width: '18px',
                      height: 'auto',
                      transform: 'scaleX(-1)',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '18px',
                  }}
                >
                  {heroTags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        padding: '4px 14px',
                        fontSize: '12px',
                        color: '#333',
                        backgroundColor: '#fff',
                        borderRadius: '999px',
                        border: '1px solid #ddd',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <img
                  src={fvOfferBanner}
                  alt="マイくるプラスのサービス"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: '8px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#1E55E6',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                >
                  {isSameCondition
                    ? '今ならお得にサブスクへ移行できます'
                    : 'サブスクにお申し込みいただけます'}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    color: '#555',
                    lineHeight: 1.7,
                  }}
                >
                  {isSameCondition
                    ? '保証満期から1か月以内限定。通常のサブスクよりお得に、既存保証の月額と修理上限金額を引き継いで申し込めます。'
                    : '保証満期から1か月以内のため、この車両でサブスクリプションにお申し込みいただけます。'}
                </p>
              </div>

              <div style={{ margin: '0 -16px 24px', padding: '24px 16px' }}>
                <SectionHead
                  enTitle="PROBLEM"
                  jaTitle="こんな経験、ありませんか？"
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {problemItems.map((problem) => (
                    <div
                      key={problem.title}
                      style={{
                        padding: '18px 20px',
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        border: '1px solid #eee',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          gap: '14px',
                          alignItems: 'flex-start',
                        }}
                      >
                        <div
                          style={{
                            width: '4px',
                            minHeight: '48px',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '2px',
                            flexShrink: 0,
                          }}
                        />
                        <div>
                          <div
                            style={{
                              fontSize: '15px',
                              fontWeight: 'bold',
                              marginBottom: '6px',
                              color: '#222',
                              lineHeight: 1.5,
                            }}
                          >
                            {problem.title}
                          </div>
                          <div
                            style={{
                              fontSize: '13px',
                              color: '#777',
                              lineHeight: 1.6,
                            }}
                          >
                            {problem.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ margin: '0 -16px 24px', padding: '24px 16px' }}>
                <SectionHead enTitle="SERVICE" jaTitle="サービス内容" />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  {serviceContents.map((service) => (
                    <div
                      key={service.title}
                      style={{
                        padding: '20px',
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        border: '1px solid #eee',
                      }}
                    >
                      <img
                        src={service.image}
                        alt={service.title}
                        style={{
                          width: '100%',
                          height: '160px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          marginBottom: '14px',
                          display: 'block',
                        }}
                      />
                      <div
                        style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          color: '#222',
                        }}
                      >
                        {service.title}
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#666',
                          lineHeight: 1.7,
                          marginBottom: '14px',
                        }}
                      >
                        {service.description}
                      </div>
                      {service.included.map((item) => (
                        <div
                          key={`included-${item}`}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            fontSize: '13px',
                            color: '#222',
                            lineHeight: 1.6,
                            marginBottom: '6px',
                          }}
                        >
                          <span style={{ flexShrink: 0 }}>●</span>
                          <span>{item}</span>
                        </div>
                      ))}
                      {service.excluded.map((item) => (
                        <div
                          key={`excluded-${item}`}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            fontSize: '13px',
                            color: '#D32F2F',
                            lineHeight: 1.6,
                            marginBottom: '6px',
                          }}
                        >
                          <span style={{ flexShrink: 0 }}>×</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  backgroundColor: '#F6F8FF',
                  border: '1px solid #E0E7FF',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '4px',
                  }}
                >
                  対象車両
                </div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  {carLabel || '選択中の車両'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {selectedCar?.class_no} {selectedCar?.hiragana}{' '}
                  {selectedCar?.registry_no}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                {detailItems.map((item) => (
                  <div
                    key={item.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '12px 0',
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <span style={{ color: '#666', fontSize: '13px' }}>
                      {item.label}
                    </span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        textAlign: 'right',
                      }}
                    >
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>

              {planError && !isSameCondition && (
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#D32F2F',
                      marginBottom: '8px',
                    }}
                  >
                    プラン情報を取得できませんでした
                  </div>
                  <button
                    onClick={fetchPlan}
                    style={{
                      padding: '8px 16px',
                      fontSize: '13px',
                      color: '#1E55E6',
                      backgroundColor: '#fff',
                      border: '1px solid #1E55E6',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    再読み込み
                  </button>
                </div>
              )}

              <button
                onClick={handleClickApply}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#fff',
                  backgroundColor: '#1E55E6',
                  border: 'none',
                  borderRadius: '28px',
                  cursor: 'pointer',
                }}
              >
                {isSameCondition ? 'お得な条件で申し込む' : '申し込みへ進む'}
              </button>
            </>
          )}

          {isLoaded && !inheritanceStatus?.eligible && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: 'bold',
                  marginBottom: '8px',
                }}
              >
                満期保証からの申込対象外です
              </div>
              <p
                style={{
                  margin: '0 0 20px',
                  fontSize: '13px',
                  color: '#666',
                  lineHeight: 1.6,
                }}
              >
                保証満期から1か月を超えている場合は、通常のサブスクリプション申込をご利用ください。
              </p>
              <button
                onClick={() =>
                  f7.view.main.router.navigate(paths.subscriptionDetail)
                }
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#1E55E6',
                  backgroundColor: '#fff',
                  border: '1px solid #1E55E6',
                  borderRadius: '28px',
                  cursor: 'pointer',
                }}
              >
                サブスク詳細へ
              </button>
            </div>
          )}
        </Paper>
      </div>
    </PageTemplate>
  );
};
